import { Textarea } from "@/components/ui/textarea";
import update from "immutability-helper";
import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Exercise, Workout } from "./workoutsColumns";
import { Tables } from "@/lib/supabase/supabaseTypes";
import ExerciseDialog from "../exercises/exerciseDialog";
import ExerciseDetailDialog from "./ExerciseDetailDialog";
import InputFieldEditButton from "../inputFieldEditButton";
import {
  useDndContext,
  useDraggable,
  useDroppable,
  type UniqueIdentifier,
} from "@dnd-kit/core";
import ExerciseRow from "./exerciseRow";
import { Button } from "../ui/button";
import {
  Dumbbell,
  Link,
  MoreHorizontal,
  PlusIcon,
  Trash2,
  Upload,
} from "lucide-react";
import ExerciseSelectDialog from "./exerciseSelectDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import SaveToLibraryDialog from "./saveToLibraryDialog";
import { Card } from "../ui/card";
import { cn } from "@/lib/utils/ui";
import { motion } from "framer-motion";
import { useWorkoutEditorWorkspace } from "./WorkoutEditorWorkspace";
import {
  DROP_ANIMATION_DURATION_MS,
  ExerciseDragData,
  getGroupDragId,
  getGroupKey,
  GroupDragData,
  WorkoutDropZoneData,
} from "./workoutEditorDnd";
import { createWorkoutExercise } from "./workoutEditorUtils";
import { useOrg } from "@/lib/states/session/hooks";
import { trpc } from "@/lib/trpc/client";
import { useVersionHistoryPreview } from "@/components/planHistory/VersionHistoryHost";

const WORKOUT_LAYOUT_TRANSITION = {
  duration: DROP_ANIMATION_DURATION_MS / 1000,
  ease: [0.22, 1, 0.36, 1] as const,
};
const WORKOUT_NOTES_MAX_HEIGHT_PX = 224;

type Value = {
  value: string;
  valueChange: (value: string) => void;
};

interface EditWorkoutViewProps {
  workout: Workout;
  updateWorkout: (workout: Workout) => void;
  name: Value;
  description: Value;
  hasChanges: boolean;
  setHasChanges: (hasChanges: boolean) => void;
  removeExercise: (Exercise: Exercise) => void;
  template?: boolean;
  deleteWorkout?: () => void;
  readOnly?: boolean;
  clientId?: string;
}

type WorkoutDropZoneProps = {
  id: UniqueIdentifier;
  data: WorkoutDropZoneData;
  enabled: boolean;
  compact?: boolean;
  hidden?: boolean;
  reserveSpace?: boolean;
  inLinkSlot?: boolean;
};

const WorkoutDropZone = ({
  id,
  data,
  enabled,
  compact = false,
  hidden = false,
  reserveSpace = false,
  inLinkSlot = false,
}: WorkoutDropZoneProps) => {
  if (hidden) {
    return (
      <div
        aria-hidden="true"
        className={cn(
          "pointer-events-none relative overflow-visible",

          compact ? "px-2" : "",
          inLinkSlot
            ? "h-10 w-full"
            : reserveSpace
              ? "my-0 h-10 opacity-0"
              : "my-0 h-0 opacity-0",
        )}
      />
    );
  }

  const { setNodeRef } = useDroppable({
    id,
    data,
    disabled: !enabled,
  });
  const { active, over } = useDndContext();
  const isDragging = Boolean(active) && enabled;
  const isOver = over?.id === id;
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (!isOver) {
      setIsExpanded(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setIsExpanded(true);
    }, 200);

    return () => {
      window.clearTimeout(timeoutId);
      setIsExpanded(false);
    };
  }, [isOver]);

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "pointer-events-none relative overflow-visible transition-all duration-200 ease-out",
        compact ? "px-2" : "",
        inLinkSlot
          ? ["h-10 w-full", isOver && (isExpanded ? "h-16" : "h-10")]
          : [
              isDragging
                ? reserveSpace
                  ? "my-0 h-10 opacity-0"
                  : compact
                    ? "my-1 h-4 opacity-0"
                    : "my-1.5 h-5 opacity-0"
                : "my-0 h-0 opacity-0",
              isOver &&
                (isExpanded
                  ? compact
                    ? "my-2 h-8 opacity-100"
                    : "my-2 h-10 opacity-100"
                  : compact
                    ? "my-1 h-4 opacity-100"
                    : "my-1.5 h-5 opacity-100"),
            ],
      )}
    >
      <div
        className={cn(
          "pointer-events-none absolute inset-x-0 top-1/2 flex select-none -translate-y-1/2 items-center justify-center rounded-lg border border-dashed text-[10px] font-semibold uppercase tracking-[0.2em] transition-all duration-200",
          inLinkSlot
            ? isOver
              ? isExpanded
                ? "h-12 px-4"
                : "h-10 px-4"
              : "inset-0 h-full translate-y-0 px-4"
            : isOver
              ? compact
                ? isExpanded
                  ? "h-8 px-3"
                  : "h-4 px-3"
                : isExpanded
                  ? "h-10 px-4"
                  : "h-5 px-4"
              : compact
                ? "h-4 px-3"
                : "h-5 px-4",
          isOver
            ? "border-primary/70 bg-primary/10 text-primary opacity-100"
            : "border-transparent bg-transparent text-primary opacity-0",
        )}
      >
        <span
          className={cn(
            "pointer-events-none transition-all duration-150",
            isExpanded
              ? "translate-y-0 opacity-100 delay-75"
              : "translate-y-0.5 opacity-0 delay-0",
          )}
        >
          {data.type === "superset-insert" ? "Drop into superset" : "Drop here"}
        </span>
      </div>
    </div>
  );
};

type SupersetShellProps = {
  id: string;
  data: GroupDragData;
  disabled: boolean;
  isDragSource: boolean;
  hasLinkBelow: boolean;
  isRecentlyDropped?: boolean;
  children: React.ReactNode;
};

const SupersetShell = ({
  id,
  data,
  disabled,
  isDragSource,
  hasLinkBelow,
  isRecentlyDropped = false,
  children,
}: SupersetShellProps) => {
  const { setNodeRef, listeners, attributes } = useDraggable({
    id,
    data,
    disabled,
  });

  return (
    <motion.div
      initial={false}
      animate={
        isRecentlyDropped
          ? { opacity: [0.45, 1] }
          : { opacity: isDragSource ? 0.3 : 1 }
      }
      transition={WORKOUT_LAYOUT_TRANSITION}
    >
      <Card
        ref={setNodeRef}
        {...(!disabled ? listeners : {})}
        {...(!disabled ? attributes : {})}
        className={cn(
          "rounded-2xl border-primary/20 bg-primary/5 p-3 transition-[color,background-color,border-color,opacity,box-shadow] duration-200 select-none touch-manipulation",
          !hasLinkBelow && "mb-3",
          !disabled && "cursor-grab active:cursor-grabbing",
          isDragSource && "border-foreground/20 bg-muted/70 opacity-45",
        )}
      >
        <div className="mb-3 flex items-center">
          <span className="rounded-full bg-primary/15 px-3 py-1 text-xs font-medium uppercase tracking-[0.2em] text-primary">
            Superset
          </span>
        </div>
        <div data-no-drag="true">{children}</div>
      </Card>
    </motion.div>
  );
};

const EditWorkoutView: FunctionComponent<EditWorkoutViewProps> = ({
  workout,
  updateWorkout,
  name,
  description,
  setHasChanges,
  removeExercise,
  template = false,
  deleteWorkout,
  readOnly: readOnlyProp = false,
  clientId,
}) => {
  const isPreviewing = useVersionHistoryPreview();
  const readOnly = readOnlyProp || isPreviewing;
  const { activeDrag, recentDrop } = useWorkoutEditorWorkspace();
  const org = useOrg();
  const descriptionRef = useRef<HTMLTextAreaElement | null>(null);
  const [openExerciseDialog, setOpenExerciseDialog] = useState(false);
  const [openExerciseSelectDialog, setOpenExerciseSelectDialog] =
    useState(false);
  const [openSaveLibraryDialog, setopenSaveLibraryDialog] = useState<Workout>();
  const [replacingExercise, setReplacingExercise] = useState<
    Exercise | undefined
  >();
  const [exerciseToView, setExerciseToView] = useState<Exercise>();
  const [exerciseDetail, setExerciseDetail] = useState<Exercise | null>(null);

  const { data: clientUnit } = trpc.clients.get.useQuery(
    { clientId: clientId ?? "" },
    { enabled: Boolean(clientId) },
  );

  const weightUnitLabel =
    clientUnit?.weight_unit ?? org?.weightUnitDefault ?? "kg";
  const distanceUnitLabel =
    clientUnit?.distance_unit === "mi"
      ? "mi"
      : clientUnit?.distance_unit === "km"
        ? "km"
        : org?.distanceUnitDefault === "mi"
          ? "mi"
          : "km";

  const handleExerciseDialogOpenChange = (open: boolean) => {
    setOpenExerciseDialog(open);
    setExerciseToView(undefined);
  };

  const updateExercise = useCallback(
    (exercise: Exercise, ex_order: number, sub_order: number) => {
      const newExercises = [...workout.exerciseCollection];
      newExercises[ex_order][sub_order] = exercise;
      const newWorkout = update(workout, {
        exerciseCollection: { $set: newExercises },
      });
      updateWorkout(newWorkout);
      setHasChanges(true);
    },
    [updateWorkout, workout, setHasChanges],
  );

  const addExercise = (exercise: Tables<"exercises">) => {
    const newExercise = createWorkoutExercise({
      exercise,
      exOrder: workout.exerciseCollection?.length ?? 0,
      subOrder: 0,
    });
    updateWorkout(
      update(workout, { exerciseCollection: { $push: [[newExercise]] } }),
    );
    setHasChanges(true);
  };

  const removeFromSuperset = useCallback(
    (exercise: Exercise) => {
      const newExercises = [...workout.exerciseCollection];
      newExercises[exercise.ex_order] = newExercises[exercise.ex_order]
        .filter((ex) => ex.id !== exercise.id)
        .map((ex, i) => {
          return { ...ex, sub_order: i };
        });

      if (exercise.sub_order === 0) {
        newExercises.splice(exercise.ex_order, 0, [exercise]);
      } else {
        newExercises.splice(exercise.ex_order + 1, 0, [exercise]);
      }

      newExercises.forEach((ex, i) => {
        ex.forEach((e, j) => {
          e.ex_order = i;
          e.sub_order = j;
        });
      });

      const newWorkout = update(workout, {
        exerciseCollection: { $set: newExercises },
      });
      updateWorkout(newWorkout);
      setHasChanges(true);
    },
    [updateWorkout, workout, setHasChanges],
  );

  const addToSuperset = useCallback(
    (nextExercise: Exercise, cur_order: number, cur_sub_order: number) => {
      const newExercises = [...workout.exerciseCollection];
      const currentGroup = newExercises[cur_order];
      const nextGroup = newExercises[nextExercise.ex_order];
      const nextGroupIsSuperset = nextGroup.length > 1;

      if (nextGroupIsSuperset) {
        const [exerciseToMove] = currentGroup.splice(cur_sub_order, 1);
        if (!exerciseToMove) {
          return;
        }

        nextGroup.push({
          ...exerciseToMove,
          ex_order: nextExercise.ex_order,
          sub_order: nextGroup.length,
        });

        if (currentGroup.length === 0) {
          newExercises.splice(cur_order, 1);
        }
      } else {
        const newExercise: Exercise = {
          ...nextExercise,
          ex_order: cur_order,
          sub_order: cur_sub_order + 1,
        };

        currentGroup.splice(cur_sub_order + 1, 0, newExercise);

        if (nextGroup.length === 1) {
          newExercises.splice(nextExercise.ex_order, 1);
        } else {
          nextGroup.splice(nextExercise.sub_order, 1);
        }
      }

      newExercises.forEach((ex, i) => {
        ex.forEach((e, j) => {
          e.ex_order = i;
          e.sub_order = j;
        });
      });

      const newWorkout = update(workout, {
        exerciseCollection: { $set: newExercises },
      });
      updateWorkout(newWorkout);
      setHasChanges(true);
    },
    [updateWorkout, workout, setHasChanges],
  );

  const replaceExercise = useCallback(
    (order: number, sub_order: number, exercise: Tables<"exercises">) => {
      const newExercises = [...workout.exerciseCollection];
      const oldExercise = newExercises[order][sub_order];
      newExercises[order][sub_order] = {
        ...oldExercise,
        ...exercise,
        ex_order: order,
        sub_order: sub_order,
      };
      const newWorkout = update(workout, {
        exerciseCollection: { $set: newExercises },
      });
      updateWorkout(newWorkout);
      setHasChanges(true);
    },
    [updateWorkout, workout, setHasChanges],
  );

  const handleExerciseSelectDialogOpenChange = (open: boolean) => {
    setOpenExerciseSelectDialog(open);
  };

  const handleExerciseSelected = (exercise: Tables<"exercises">) => {
    setOpenExerciseSelectDialog(false);

    if (replacingExercise) {
      replaceExercise(
        replacingExercise.ex_order,
        replacingExercise.sub_order,
        exercise,
      );
      setReplacingExercise(undefined);
      return;
    }
    addExercise(exercise);
  };

  const handleExerciseCreated = (exercise: Tables<"exercises">) => {
    if (replacingExercise) {
      replaceExercise(
        replacingExercise.ex_order,
        replacingExercise.sub_order,
        exercise,
      );
      setReplacingExercise(undefined);
      return;
    }
    addExercise(exercise);
  };

  const hasExerciseDrag = activeDrag?.type === "exercise";
  const hasLibraryExerciseDrag = activeDrag?.type === "library-exercise";
  const hasGroupDrag = activeDrag?.type === "group";
  const showDropZones = !readOnly && Boolean(activeDrag);
  const groupInsertEnabled = showDropZones;
  const supersetInsertEnabled =
    showDropZones && (hasExerciseDrag || hasLibraryExerciseDrag);
  const hiddenGroupInsertIndices = new Set<number>();

  if (activeDrag?.type === "group") {
    hiddenGroupInsertIndices.add(activeDrag.sourceGroupIndex);
    hiddenGroupInsertIndices.add(activeDrag.sourceGroupIndex + 1);
  }

  if (
    activeDrag?.type === "exercise" &&
    workout.exerciseCollection[activeDrag.sourceGroupIndex]?.length === 1
  ) {
    hiddenGroupInsertIndices.add(activeDrag.sourceGroupIndex);
    hiddenGroupInsertIndices.add(activeDrag.sourceGroupIndex + 1);
  }

  const isSupersetInsertHidden = (groupIndex: number, position: number) => {
    if (activeDrag?.type !== "exercise") {
      return false;
    }

    if (activeDrag.sourceGroupIndex !== groupIndex) {
      return false;
    }

    const sourceGroup = workout.exerciseCollection[groupIndex];
    if (!sourceGroup || sourceGroup.length <= 1) {
      return false;
    }

    return (
      position === activeDrag.sourceExerciseIndex ||
      position === activeDrag.sourceExerciseIndex + 1
    );
  };

  useEffect(() => {
    const textarea = descriptionRef.current;

    if (!textarea) {
      return;
    }

    textarea.style.height = "0px";
    textarea.style.height = `${Math.min(
      textarea.scrollHeight,
      WORKOUT_NOTES_MAX_HEIGHT_PX,
    )}px`;
    textarea.style.overflowY =
      textarea.scrollHeight > WORKOUT_NOTES_MAX_HEIGHT_PX ? "auto" : "hidden";
  }, [description.value]);

  return (
    <>
      <ExerciseDialog
        open={openExerciseDialog}
        onOpenChange={handleExerciseDialogOpenChange}
        onExerciseCreated={handleExerciseCreated}
        exercise={exerciseToView}
      />
      <ExerciseSelectDialog
        open={openExerciseSelectDialog}
        onOpenChange={handleExerciseSelectDialogOpenChange}
        exerciseSelected={handleExerciseSelected}
        createExerciseSelected={() => {
          setOpenExerciseSelectDialog(false);
          setOpenExerciseDialog(true);
        }}
      />
      <SaveToLibraryDialog
        workout={openSaveLibraryDialog}
        onClose={() => {
          setopenSaveLibraryDialog(undefined);
        }}
      />
      <ExerciseDetailDialog
        open={Boolean(exerciseDetail)}
        exercise={exerciseDetail}
        clientId={clientId}
        weightUnitLabel={weightUnitLabel}
        distanceUnitLabel={distanceUnitLabel}
        showEditExerciseButton={!readOnly}
        onEditExercise={() => {
          if (!exerciseDetail) return;
          setExerciseToView(exerciseDetail);
          setExerciseDetail(null);
          setOpenExerciseDialog(true);
        }}
        onOpenChange={(open) => {
          if (!open) {
            setExerciseDetail(null);
          }
        }}
        analyticsName="edit_workout_exercise_detail_dialog"
      />

      <div className="flex min-w-0 flex-col gap-2 overflow-visible">
        <div className="my-2 flex justify-between px-3 sm:px-0">
          <InputFieldEditButton
            value={name.value}
            setValue={(value) => {
              name.valueChange(value);
              setHasChanges(true);
            }}
            placeholder={"New Training Plan"}
            className="text-2xl font-bold"
            disabled={readOnly}
          />
          {!template && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="">
                  <span className="sr-only">Open menu</span>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => {
                    setopenSaveLibraryDialog(workout);
                  }}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  Save to Library
                </DropdownMenuItem>
                {!readOnly ? (
                  <DropdownMenuItem
                    onClick={() => {
                      deleteWorkout?.();
                    }}
                    variant="destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete Workout
                  </DropdownMenuItem>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {!readOnly || description.value.trim().length > 0 ? (
          <div className="px-3 sm:px-0">
            <Textarea
              ref={descriptionRef}
              value={description.value}
              onChange={(e) => {
                description.valueChange(e.target.value);
                setHasChanges(true);
              }}
              placeholder="Add notes..."
              disabled={readOnly}
              className="min-h-[96px] resize-none overflow-y-hidden border-0 bg-muted/60 shadow-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0"
            />
          </div>
        ) : null}

        <div className="mb-10 mt-4 px-3 pb-32 sm:px-0">
          {workout.exerciseCollection.length === 0 && (
            <Card className="flex h-full w-full flex-col items-center justify-center p-4">
              <Dumbbell className="my-4 h-10 w-10 text-primary" />
              <h1 className="font-medium">No Exercises</h1>
              <p className="mb-4 text-gray-500">
                No exercises have been added to this workout yet.
              </p>
            </Card>
          )}

          <div className="space-y-0">
            <WorkoutDropZone
              id="group-insert-0"
              data={{ type: "group-insert", index: 0 }}
              enabled={groupInsertEnabled}
              hidden={hiddenGroupInsertIndices.has(0)}
            />

            {workout.exerciseCollection.map((group, index) => {
              const groupLength = group.length;
              const isSuperset = groupLength > 1;
              const groupKey = getGroupKey(group);
              const groupDragId = getGroupDragId(group);
              const groupDragData: GroupDragData = {
                type: "group",
                groupId: groupDragId,
                exerciseCount: group.length,
                sourceGroupIndex: index,
              };
              const isActiveGroupDrag =
                hasGroupDrag && activeDrag?.groupId === groupDragId;
              const showSupersetLink =
                index !== workout.exerciseCollection.length - 1;
              const lastExerciseInGroup = group[groupLength - 1];

              const groupRows = (
                <div>
                  {isSuperset && (
                    <WorkoutDropZone
                      id={`superset-insert-${index}-0`}
                      data={{
                        type: "superset-insert",
                        groupIndex: index,
                        position: 0,
                      }}
                      enabled={supersetInsertEnabled}
                      compact
                      hidden={isSupersetInsertHidden(index, 0)}
                    />
                  )}

                  {group.map((exercise, sub_index) => {
                    const canShowLink =
                      !isSuperset &&
                      sub_index === groupLength - 1 &&
                      showSupersetLink;
                    const exerciseDragData: ExerciseDragData = {
                      type: "exercise",
                      exerciseId: exercise.sortableId,
                      exerciseName: exercise.name,
                      sourceGroupIndex: index,
                      sourceExerciseIndex: sub_index,
                    };

                    return (
                      <div key={exercise.sortableId}>
                        <ExerciseRow
                          exercise={exercise}
                          readOnly={readOnly}
                          updateExercise={updateExercise}
                          removeExercise={removeExercise}
                          replaceExercise={(exercise) => {
                            if (readOnly) return;
                            setReplacingExercise(exercise);
                            setOpenExerciseSelectDialog(true);
                          }}
                          exerciseSelected={(exercise) => {
                            setExerciseDetail(exercise);
                          }}
                          groupLength={groupLength}
                          removeFromSuperset={removeFromSuperset}
                          canShowLink={canShowLink}
                          dragId={`exercise-${exercise.sortableId}`}
                          dragData={exerciseDragData}
                          dragDisabled={readOnly || isActiveGroupDrag}
                          isRecentlyDropped={
                            recentDrop?.type === "exercise" &&
                            recentDrop.exerciseId === exercise.sortableId
                          }
                          clientId={clientId}
                          weightUnitLabel={weightUnitLabel}
                          distanceUnitLabel={distanceUnitLabel}
                        />
                        {isSuperset && (
                          <WorkoutDropZone
                            id={`superset-insert-${index}-${sub_index + 1}`}
                            data={{
                              type: "superset-insert",
                              groupIndex: index,
                              position: sub_index + 1,
                            }}
                            enabled={supersetInsertEnabled}
                            compact
                            hidden={isSupersetInsertHidden(
                              index,
                              sub_index + 1,
                            )}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              );

              return (
                <div key={groupKey}>
                  {isSuperset ? (
                    <SupersetShell
                      id={groupDragId}
                      data={groupDragData}
                      disabled={readOnly}
                      isDragSource={isActiveGroupDrag}
                      hasLinkBelow={showSupersetLink && !readOnly}
                      isRecentlyDropped={
                        recentDrop?.type === "group" &&
                        recentDrop.groupId === groupDragId
                      }
                    >
                      {groupRows}
                    </SupersetShell>
                  ) : (
                    groupRows
                  )}

                  {showSupersetLink && !readOnly ? (
                    <div className="relative" key={`${groupKey}-between`}>
                      <div
                        className={cn(
                          "absolute inset-0 flex items-center justify-center transition-opacity duration-150",
                          activeDrag && "pointer-events-none opacity-0",
                        )}
                        aria-hidden={activeDrag ? "true" : undefined}
                      >
                        <Button
                          variant="ghost"
                          tabIndex={activeDrag ? -1 : undefined}
                          onClick={() => {
                            const nextExercise =
                              workout.exerciseCollection[index + 1][0];
                            addToSuperset(
                              nextExercise,
                              lastExerciseInGroup.ex_order,
                              lastExerciseInGroup.sub_order,
                            );
                          }}
                        >
                          <Link />
                        </Button>
                      </div>
                      <WorkoutDropZone
                        id={`group-insert-${index + 1}`}
                        data={{ type: "group-insert", index: index + 1 }}
                        enabled={groupInsertEnabled}
                        hidden={hiddenGroupInsertIndices.has(index + 1)}
                        inLinkSlot
                      />
                    </div>
                  ) : (
                    <WorkoutDropZone
                      id={`group-insert-${index + 1}`}
                      data={{ type: "group-insert", index: index + 1 }}
                      enabled={groupInsertEnabled}
                      hidden={hiddenGroupInsertIndices.has(index + 1)}
                      reserveSpace={false}
                    />
                  )}
                </div>
              );
            })}
          </div>

          {!readOnly ? (
            <div className="mt-6 flex justify-center">
              <Button
                variant="outline"
                className="inline-flex items-center justify-center gap-2"
                onClick={() => setOpenExerciseSelectDialog(true)}
              >
                <PlusIcon className="h-4 w-4 shrink-0 opacity-50" />
                Add Exercise
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default EditWorkoutView;
